import { Trans, useTranslation } from '@repo/translations/use-translation';
import { lazy, Suspense } from 'react';

import { TextBadge } from '@zooplus/ui-components';

import { MembershipsComparison } from '../../../../hooks/useMembershipsComparison';
import { ProgramLogo } from '../../../UI/ProgramLogo/ProgramLogo';

const BasicCard = lazy(() => import('./basic-card-icon.svg'));
const PremiumCard = lazy(() => import('./premium-card-icon.svg'));

import styles from './ProgramPlanHeader.module.css';
import { OfferPrice } from '../../../UI/OfferPrice';

export interface ProgramPlanHeaderProps {
  membershipsComparison: MembershipsComparison;
  levelType: string;
}

export const ProgramPlanHeader = ({ membershipsComparison, levelType }: ProgramPlanHeaderProps) => {
  const { t } = useTranslation();

  const renderPrice = (membershipsComparison: MembershipsComparison, levelType: string) => {
    if (levelType === 'BASIC') {
      return (
        <span className={styles.priceFree}>
          {t('loyalty-program.public-landing-page.program-plans.header.level-price-free')}
        </span>
      );
    }
    return (
      <span className={styles.pricePerYear}>
        <Trans
          i18nKey={'loyalty-program.public-landing-page.program-plans.header.yearly-price'}
          components={{
            Price: (
              <OfferPrice
                price={membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice?.price}
                referencePrice={
                  membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice?.referencePrice
                }
                secondaryCurrencyPrice={
                  membershipsComparison.premiumLoyaltyMembershipInfo.membershipPrice
                    ?.secondaryCurrencyPrice
                }
                size="large"
              />
            ),
          }}
        />
      </span>
    );
  };

  return (
    <div className={styles.planHeader}>
      <div className={styles.logoAndLevelContainer}>
        <Suspense fallback={null}>
          <ProgramLogo height={19} />
        </Suspense>
        <h2 className={styles.loyaltyLevel}>
          {levelType === 'BASIC'
            ? t('loyalty-program.public-landing-page.program-plans.header.basic-level')
            : t('loyalty-program.public-landing-page.program-plans.header.premium-level')}
        </h2>
      </div>
      <div className={styles.cardContainer}>
        <Suspense fallback={null}>
          {levelType && levelType === 'BASIC' ? (
            <BasicCard
              data-testid={'basic-card'}
              className={styles.card}
              title="Basic Loyalty Program Card icon"
            />
          ) : (
            <PremiumCard
              data-testid={'premium-card'}
              className={styles.card}
              title="Premium Loyalty Program Card icon"
            />
          )}
        </Suspense>
      </div>
      <div className={styles.priceContainer}>
        {renderPrice(membershipsComparison, levelType)}
        {membershipsComparison.customerLoyaltyLevelType === levelType && (
          <TextBadge
            className={styles.currentPlan}
            label={t('loyalty-program.public-landing-page.program-plans.current-plan')}
            variant="info"
          />
        )}
      </div>
    </div>
  );
};
